/**
 * formatAppliedDateRange — an unambiguous, locale-independent, timezone-free echo
 * of an APPLIED (running) date range, with the month spelled out.
 *
 * WHY (DATEFMT-UNAMBIGUOUS, follows #284/#285): a native `type="date"` input
 * renders its display in the OS locale (en-US → mm/dd/yyyy). A user thinking in
 * dd/mm/yyyy can pick 03/04, believe "3 April", actually filter "4 March", get
 * plausible numbers, and get NO signal. Silent wrong data — the same failure
 * class #285's explicit Apply exists to prevent. We can't change the field's
 * format (the browser owns it), so we make it irrelevant by echoing the range
 * back with the month named.
 *
 * TWO HARD RULES this file exists to enforce:
 *   1. NO `new Date(iso)`: `new Date("2026-07-01")` parses as UTC midnight, so in
 *      any negative-offset TZ (e.g. ET) `toLocaleDateString` renders it "30 Jun"
 *      — an off-by-one echo that MANUFACTURES the misreading we're removing. We
 *      split the ISO string and index a fixed month array. No Date, no locale
 *      API, no timezone anywhere in the path.
 *   2. The caller MUST pass the APPLIED values (never the live input state) — see
 *      the callers; this module only formats what it's given.
 *
 * General on purpose: no dashboard/report-specific assumptions (this lands on
 * ~6 surfaces — CRM filter bar, CEO dashboard, every report drill-down).
 */

// Fixed English month abbreviations — deterministic, NOT locale-derived.
export const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format one 'YYYY-MM-DD' string as 'D Mon YYYY' (e.g. '2026-07-01' → '1 Jul 2026').
 * No Date object, no locale API. Returns null for anything not a well-formed
 * ISO date (so a malformed value never renders a wrong-but-plausible date).
 *
 * Day range-check is structural only (01–31), NOT per-month — so an impossible
 * date like '2026-02-31' would render '31 Feb 2026' rather than null. This is
 * deliberate: doing real calendar validation would require a Date object, which
 * this module exists to avoid. It is unreachable in practice — a native
 * type="date" input cannot emit an impossible date, and preset ranges are
 * validated (isValidRange) before they are applied — so the input is always a
 * real calendar date. Documented so the claim matches the behaviour.
 */
export function formatISODate(iso) {
  if (typeof iso !== 'string') return null;
  const parts = iso.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts;
  if (!/^\d{4}$/.test(y) || !/^\d{2}$/.test(m) || !/^\d{2}$/.test(d)) return null;
  const mi = Number(m);
  const di = Number(d);
  if (mi < 1 || mi > 12 || di < 1 || di > 31) return null;
  return `${di} ${MONTH_ABBR[mi - 1]} ${y}`;
}

/**
 * Echo an applied range. States (each rendered honestly — never claims a range
 * that is not actually running):
 *   - both ends, from <= to : "Showing 1 Jul 2026 - 24 Jul 2026"
 *   - FROM only             : "Showing from 1 Jul 2026"
 *   - TO only               : "Showing through 24 Jul 2026"
 *   - neither               : ""  (render nothing — no range is active)
 *   - both ends but inverted (from > to) or malformed : ""  (not a runnable range)
 *
 * @param {string|null|undefined} appliedFrom  APPLIED from (YYYY-MM-DD) — NOT the live input
 * @param {string|null|undefined} appliedTo    APPLIED to   (YYYY-MM-DD) — NOT the live input
 * @returns {string} the echo, or '' when nothing should be shown
 */
export function formatAppliedDateRange(appliedFrom, appliedTo) {
  const from = appliedFrom ? formatISODate(appliedFrom) : null;
  const to = appliedTo ? formatISODate(appliedTo) : null;

  if (from && to) {
    // Only claim a two-sided range when it is chronologically runnable
    // (string compare is safe on YYYY-MM-DD). Inverted → claim nothing.
    return appliedFrom <= appliedTo ? `Showing ${from} - ${to}` : '';
  }
  if (from) return `Showing from ${from}`;
  if (to) return `Showing through ${to}`;
  return '';
}
